import type { TFunction } from 'i18next';
import { formatTokens } from '../utils/format-tokens';
import { useRelativeTimeLabel } from '../utils/use-relative-time';

// Ledger types with a translated label of their own. Anything else falls back
// to the generic "token activity" wording rather than rendering a raw key.
const KNOWN_LEDGER_TYPES = new Set([
  'credit_unmatched',
  'credit_refund',
  'credit_cheer',
  'credit_sub',
  'credit_gift_sub',
  'credit_resub',
  'credit_admin',
  'debit_request',
  'debit_learn_vote',
  'credit_external',
  'debit_external',
  'debit_admin',
]);

export type TokenLedgerRowProps = {
  amount: number;
  type: string;
  createdAt: string;
  clientName?: string | null;
  /** Streamer or moderator behind a manual adjustment. */
  adminUsername?: string | null;
  t: TFunction;
};

/**
 * One row of a token ledger. Shared by the viewer-facing balance popover and
 * the streamer's per-holder detail page so both name each ledger type the same
 * way.
 */
export function TokenLedgerRow({
  amount,
  type,
  createdAt,
  clientName,
  adminUsername,
  t,
}: TokenLedgerRowProps) {
  const relativeLabel = useRelativeTimeLabel(createdAt);
  const credit = amount >= 0;
  const app = clientName ?? t('tokens.popover.externalApp');
  const admin = adminUsername ?? t('tokens.popover.unknownAdmin');
  const labelKey = KNOWN_LEDGER_TYPES.has(type) ? type : 'other';

  return (
    <div className='flex items-baseline gap-2'>
      <span
        className={`min-w-14 font-mono text-[13px] font-bold ${
          credit ? 'text-green-600' : 'text-(--token-accent)'
        }`}
      >
        {credit ? `+${formatTokens(amount)}` : `−${formatTokens(-amount)}`}
      </span>
      <span>{t(`tokens.popover.type.${labelKey}`, { app, admin })}</span>
      <span className='ml-auto text-xs text-muted-foreground'>{relativeLabel}</span>
    </div>
  );
}
